import { useState } from 'react';
import {
    Box, Button, Typography, TextField, Grid,
    Select, MenuItem, FormControl, InputLabel, IconButton, CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { AdminApi } from '../../../../services/api';
import { ALLOWED_TABLES, btnSx, inputSx } from './adminConstants';

function DbExplorer() {
    const [table, setTable] = useState('');
    const [fields, setFields] = useState('');
    const [orderBy, setOrderBy] = useState('');
    const [orderDir, setOrderDir] = useState('ASC');
    const [limit, setLimit] = useState(50);
    const [filters, setFilters] = useState([]);
    const [dbCols, setDbCols] = useState([]);
    const [result, setResult] = useState(null);
    const [querying, setQuerying] = useState(false);
    const [error, setError] = useState('');

    const handleTableChange = async (val) => {
        setTable(val);
        setResult(null);
        setError('');
        setFilters([]);
        setDbCols([]);
        if (!val) return;
        try {
            const params = new URLSearchParams({ table: val, limit: 0 });
            const res = await AdminApi.getIndividual(params.toString());
            setDbCols(res.data.columns || []);
        } catch { }
    };

    const addFilter = () => setFilters(prev => [...prev, { col: '', val: '' }]);
    const updateFilter = (i, key, val) =>
        setFilters(prev => prev.map((f, idx) => idx === i ? { ...f, [key]: val } : f));
    const removeFilter = (i) => setFilters(prev => prev.filter((_, idx) => idx !== i));

    const runQuery = async () => {
        if (!table) return;
        setQuerying(true);
        setError('');
        setResult(null);
        try {
            const params = new URLSearchParams({ table, limit });
            if (fields.trim()) params.set('fields', fields.trim());
            if (orderBy.trim()) params.set('order_by', orderBy.trim());
            params.set('order_dir', orderDir);
            const validFilters = filters.filter(f => f.col && f.val);
            if (validFilters.length)
                params.set('filters', validFilters.map(f => `${f.col}:${f.val}`).join(','));
            const res = await AdminApi.getIndividual(params.toString());
            setResult(res.data);
        } catch (e) {
            setError(e.response?.data?.error || e.message);
        } finally {
            setQuerying(false);
        }
    };

    const resultCols = result?.rows?.length ? Object.keys(result.rows[0]) : [];

    return (
        <Box>
            {/* Tabelle + Limit */}
            <Grid container spacing={1.5} mb={1.5}>
                <Grid size={8}>
                    <FormControl fullWidth size="small" sx={inputSx}>
                        <InputLabel>Tabelle</InputLabel>
                        <Select value={table} onChange={e => handleTableChange(e.target.value)} label="Tabelle">
                            <MenuItem value=""><em>Wählen…</em></MenuItem>
                            {ALLOWED_TABLES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={4}>
                    <TextField
                        label="Limit" type="number" size="small" fullWidth
                        value={limit} onChange={e => setLimit(Math.min(500, Math.max(1, +e.target.value)))}
                        inputProps={{ min: 1, max: 500 }} sx={inputSx}
                    />
                </Grid>
            </Grid>

            {/* Felder */}
            <TextField
                label="Felder (leer = alle)" size="small" fullWidth
                value={fields} onChange={e => setFields(e.target.value)}
                placeholder="z.B. id, name, email" sx={{ ...inputSx, mb: 1.5 }}
            />

            {/* Sortierung */}
            <Grid container spacing={1.5} mb={1.5}>
                <Grid size={8}>
                    <TextField
                        label="Sortierung nach" size="small" fullWidth
                        value={orderBy} onChange={e => setOrderBy(e.target.value)}
                        placeholder="z.B. erstellt_am" sx={inputSx}
                    />
                </Grid>
                <Grid size={4}>
                    <FormControl fullWidth size="small" sx={inputSx}>
                        <InputLabel>Richtung</InputLabel>
                        <Select value={orderDir} onChange={e => setOrderDir(e.target.value)} label="Richtung">
                            <MenuItem value="ASC">ASC</MenuItem>
                            <MenuItem value="DESC">DESC</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            {/* Filter */}
            <Typography sx={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '.07em', mb: 0.75 }}>
                Filter
            </Typography>
            {filters.map((f, i) => (
                <Grid key={i} container spacing={1} mb={0.75} alignItems="center">
                    <Grid size={5}>
                        <FormControl size="small" fullWidth sx={inputSx}>
                            <InputLabel>Spalte</InputLabel>
                            <Select value={f.col} onChange={e => updateFilter(i, 'col', e.target.value)} label="Spalte">
                                <MenuItem value=""><em>Spalte…</em></MenuItem>
                                {dbCols.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={6}>
                        <TextField
                            size="small" fullWidth placeholder="Wert" value={f.val}
                            onChange={e => updateFilter(i, 'val', e.target.value)}
                            sx={inputSx}
                        />
                    </Grid>
                    <Grid size={1} display="flex" justifyContent="center">
                        <IconButton size="small" onClick={() => removeFilter(i)}
                            sx={{ color: '#ef4444', border: '0.5px solid rgba(239,68,68,.3)', borderRadius: '8px', p: '5px' }}>
                            <CloseIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Grid>
                </Grid>
            ))}
            <Button
                fullWidth size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                onClick={addFilter}
                sx={{ mb: 1.5, fontSize: '11px', color: '#3b82f6', border: '0.5px solid rgba(59,130,246,.25)', borderRadius: '8px', textTransform: 'none', '&:hover': { background: 'rgba(59,130,246,.06)' } }}
            >
                Filter hinzufügen
            </Button>

            {/* Run */}
            <Button
                fullWidth variant="contained" disabled={!table || querying}
                startIcon={querying ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <PlayArrowIcon sx={{ fontSize: 16 }} />}
                onClick={runQuery} sx={btnSx}
            >
                {querying ? 'Lädt…' : 'Abfrage ausführen'}
            </Button>

            {/* Error */}
            {error && (
                <Typography sx={{ fontSize: '12px', color: '#ef4444', mt: 1.5, p: 1, bgcolor: 'rgba(239,68,68,.08)', borderRadius: '8px' }}>
                    {error}
                </Typography>
            )}

            {/* Ergebnis */}
            {result && (
                <Box mt={1.5}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.75}>
                        <Typography sx={{ fontSize: '11px', color: '#64748b' }}>
                            {result.count} Zeile{result.count !== 1 ? 'n' : ''}
                        </Typography>
                        <Typography sx={{ fontSize: '10px', color: '#3b82f6', bgcolor: 'rgba(59,130,246,.1)', px: 1, py: 0.25, borderRadius: '20px' }}>
                            {result.table}
                        </Typography>
                    </Box>
                    {result.rows?.length ? (
                        <Box sx={{ overflowX: 'auto', border: '0.5px solid rgba(255,255,255,.07)', borderRadius: '8px' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                                <thead>
                                    <tr>
                                        {resultCols.map(c => (
                                            <th key={c} style={{ background: 'rgba(59,130,246,.08)', color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em', padding: '7px 10px', textAlign: 'left', fontWeight: 500, whiteSpace: 'nowrap' }}>
                                                {c}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {result.rows.map((row, ri) => (
                                        <tr key={ri}>
                                            {resultCols.map(c => (
                                                <td key={c} title={String(row[c] ?? '')}
                                                    style={{ padding: '7px 10px', borderTop: '0.5px solid rgba(255,255,255,.05)', color: row[c] == null ? '#475569' : '#cbd5e1', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {row[c] == null ? 'null' : String(row[c])}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Box>
                    ) : (
                        <Typography sx={{ fontSize: '12px', color: '#475569', textAlign: 'center', py: 2 }}>
                            Keine Ergebnisse.
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
}

export default DbExplorer;
